import { useState } from "react";
import {
  Book,
  Database,
  Zap,
  Layers,
  Building2,
  Shield,
  Clock,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowRight,
  Table,
  RefreshCw,
  GitBranch,
  Users,
  Lock,
  FileText,
  Code,
  Play,
  Settings,
  Download,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";

const SectionCard = ({ icon: Icon, title, children, variant = "default" }) => (
  <Card className={variant === "highlight" ? "border-primary/50 bg-primary/5" : ""}>
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${variant === "highlight" ? "bg-primary/10" : "bg-muted"}`}>
          <Icon className={`w-5 h-5 ${variant === "highlight" ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const StepCard = ({ number, title, children }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold mb-2">{title}</h4>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  </div>
);

const ComparisonTable = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-muted">
          {headers.map((h, i) => (
            <th key={i} className="border p-3 text-left font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-muted/30"}>
            {row.map((cell, j) => (
              <td key={j} className="border p-3">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CodeBlock = ({ title, code }) => (
  <div className="rounded-lg overflow-hidden border">
    {title && (
      <div className="bg-muted px-4 py-2 border-b flex items-center gap-2">
        <Code className="w-4 h-4" />
        <span className="text-sm font-medium">{title}</span>
      </div>
    )}
    <pre className="bg-slate-900 text-slate-100 p-4 overflow-x-auto text-sm">
      <code>{code}</code>
    </pre>
  </div>
);

const FlowDiagram = ({ steps }) => (
  <div className="flex items-center gap-2 flex-wrap py-4">
    {steps.map((step, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`px-4 py-2 rounded-lg text-sm font-medium ${step.color}`}>
          {step.label}
        </div>
        {i < steps.length - 1 && <ArrowRight className="w-5 h-5 text-muted-foreground" />}
      </div>
    ))}
  </div>
);

export default function Documentation() {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", label: "Overview", icon: Book },
    { id: "cdc-methods", label: "CDC Methods", icon: Zap },
    { id: "transformations", label: "Transformations", icon: Layers },
    { id: "multi-tenant", label: "Multi-Tenant", icon: Building2 },
    { id: "rls", label: "Row-Level Security", icon: Shield },
    { id: "examples", label: "Examples", icon: FileText },
  ];

  const handleDownload = () => {
    // Create download link for documentation
    const link = document.createElement('a');
    link.href = '/docs/DATAFLOW_COMPLETE_DOCUMENTATION.md';
    link.download = 'DataFlow_Documentation.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6" data-testid="documentation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Documentation</h2>
          <p className="text-muted-foreground">
            Complete guide to CDC, Transformations, and Multi-Tenant Architecture
          </p>
        </div>
        <Button onClick={handleDownload} variant="outline" data-testid="download-docs-btn">
          <Download className="w-4 h-4 mr-2" />
          Download Documentation
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <Card className="w-64 flex-shrink-0 h-fit sticky top-6">
          <CardContent className="p-4">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeSection === section.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="flex-1 space-y-8">
          {/* Overview Section */}
          {activeSection === "overview" && (
            <div className="space-y-6">
              <SectionCard icon={Book} title="DataFlow Portal Overview" variant="highlight">
                <p className="text-muted-foreground mb-4">
                  DataFlow Portal is a comprehensive data lake management solution that enables you to build 
                  robust ETL pipelines from Oracle ERP databases to Google BigQuery with full multi-tenant support.
                </p>
                
                <h4 className="font-semibold mb-3">Key Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Zap className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Real-time CDC</p>
                      <p className="text-sm text-muted-foreground">Multiple CDC methods for different use cases</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Layers className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium">3-Stage Pipeline</p>
                      <p className="text-sm text-muted-foreground">Bronze → Silver → Gold data lake architecture</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Building2 className="w-5 h-5 text-purple-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Multi-Tenant</p>
                      <p className="text-sm text-muted-foreground">One pipeline, many tenants with data isolation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Shield className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium">Row-Level Security</p>
                      <p className="text-sm text-muted-foreground">Automatic RLS policies per tenant</p>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard icon={GitBranch} title="Data Lake Architecture">
                <FlowDiagram steps={[
                  { label: "Oracle ERP", color: "bg-orange-100 text-orange-700" },
                  { label: "CDC Replication", color: "bg-yellow-100 text-yellow-700" },
                  { label: "Bronze (Raw)", color: "bg-amber-100 text-amber-700" },
                  { label: "Silver (Clean)", color: "bg-gray-100 text-gray-700" },
                  { label: "Gold (Business)", color: "bg-yellow-200 text-yellow-800" },
                ]} />
                
                <div className="space-y-4 mt-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-amber-100 text-amber-700">Bronze Layer</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Raw data exactly as it comes from the source. No transformations applied.
                      Each tenant gets their own isolated dataset (e.g., <code className="bg-muted px-1 rounded">bronze_tenant_a</code>).
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-gray-100 text-gray-700">Silver Layer</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Cleaned and standardized data. Data types are cast, nulls are handled, and records are filtered.
                      Shared tables with <code className="bg-muted px-1 rounded">tenant_id</code> column for isolation.
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-yellow-200 text-yellow-800">Gold Layer</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Business-ready aggregated data. Joins, calculations, and KPIs.
                      Protected by Row-Level Security (RLS) policies.
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>
          )}

          {/* CDC Methods Section */}
          {activeSection === "cdc-methods" && (
            <div className="space-y-6">
              <SectionCard icon={Zap} title="CDC Methods Overview" variant="highlight">
                <p className="text-muted-foreground mb-4">
                  Change Data Capture (CDC) enables real-time or near real-time data replication from your Oracle database 
                  to BigQuery. Choose the method that best fits your requirements.
                </p>
              </SectionCard>

              {/* Google Datastream */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Zap className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle>Google Cloud Datastream</CardTitle>
                        <CardDescription>Real-time log-based CDC</CardDescription>
                      </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">Recommended</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Google Datastream reads Oracle redo logs to capture changes in real-time (5-15 second latency).
                    It is the most efficient method for high-volume, mission-critical data replication.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-green-600 flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4" /> Advantages
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Near real-time (5-15 sec latency)</li>
                        <li>• Captures INSERT, UPDATE, DELETE</li>
                        <li>• Zero impact on source database</li>
                        <li>• Automatic schema evolution</li>
                        <li>• Full Unicode/Arabic support</li>
                        <li>• Google-managed infrastructure</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-yellow-600 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" /> Requirements
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Network connectivity to Google Cloud</li>
                        <li>• Oracle ARCHIVELOG mode enabled</li>
                        <li>• Supplemental logging enabled</li>
                        <li>• LogMiner privileges for CDC user</li>
                        <li>• Firewall rules for GCP IP ranges</li>
                      </ul>
                    </div>
                  </div>

                  <CodeBlock 
                    title="Oracle Setup for Datastream"
                    code={`-- Enable supplemental logging
ALTER DATABASE ADD SUPPLEMENTAL LOG DATA;
ALTER DATABASE ADD SUPPLEMENTAL LOG DATA (ALL) COLUMNS;

-- Create CDC user with required privileges
CREATE USER datastream_user IDENTIFIED BY "secure_password";
GRANT CREATE SESSION TO datastream_user;
GRANT SELECT ANY TABLE TO datastream_user;
GRANT FLASHBACK ANY TABLE TO datastream_user;
GRANT SELECT_CATALOG_ROLE TO datastream_user;
GRANT EXECUTE_CATALOG_ROLE TO datastream_user;
GRANT SELECT ON SYS.V_$DATABASE TO datastream_user;
GRANT SELECT ON SYS.V_$ARCHIVED_LOG TO datastream_user;
GRANT SELECT ON SYS.V_$LOGMNR_CONTENTS TO datastream_user;
GRANT LOGMINING TO datastream_user;`}
                  />

                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h5 className="font-semibold text-purple-700 mb-2">When to Use</h5>
                    <p className="text-sm text-purple-600">
                      Use Google Datastream when you need real-time data synchronization, have network access 
                      from Google Cloud to your Oracle database, and want a fully managed CDC solution.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamp Polling */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Timestamp-Based Polling</CardTitle>
                      <CardDescription>Periodic change detection via timestamps</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Queries Oracle tables periodically using a timestamp column (e.g., LAST_UPDATE_DATE) 
                    to detect changed records. Simple to set up but cannot capture DELETE operations.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-green-600 flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4" /> Advantages
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• No special Oracle configuration</li>
                        <li>• Works through firewalls</li>
                        <li>• Simple to understand and debug</li>
                        <li>• Full Unicode/Arabic support</li>
                        <li>• Low privilege requirements</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-red-600 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" /> Limitations
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Cannot capture DELETEs</li>
                        <li>• Higher latency (minutes)</li>
                        <li>• Requires timestamp column</li>
                        <li>• Database load during polling</li>
                        <li>• May miss rapid updates</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-lg">
                    <h5 className="font-semibold mb-2">Common Timestamp Columns</h5>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">LAST_UPDATE_DATE</Badge>
                      <Badge variant="outline">MODIFIED_DATE</Badge>
                      <Badge variant="outline">UPD_DATE</Badge>
                      <Badge variant="outline">CREATION_DATE</Badge>
                      <Badge variant="outline">LAST_MODIFIED</Badge>
                    </div>
                  </div>

                  <CodeBlock 
                    title="How Polling Works (Internal Query)"
                    code={`-- DataFlow generates this query for each poll cycle:
SELECT *
FROM SCHEMA.TABLE_NAME
WHERE LAST_UPDATE_DATE > :last_watermark
  AND LAST_UPDATE_DATE <= :current_timestamp
ORDER BY LAST_UPDATE_DATE
FETCH FIRST :batch_size ROWS ONLY;

-- The watermark is updated after each successful batch`}
                  />

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="font-semibold text-blue-700 mb-2">When to Use</h5>
                    <p className="text-sm text-blue-600">
                      Use Timestamp Polling when you cannot set up network connectivity to Google Cloud,
                      your tables have reliable timestamp columns, and you do not need to capture DELETE operations.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Materialized View Logs */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Table className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle>Materialized View Logs</CardTitle>
                      <CardDescription>Oracle-native change tracking</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Uses Oracle is built-in Materialized View Log feature to track changes. 
                    The MView log stores all DML operations (INSERT, UPDATE, DELETE) which DataFlow reads and applies.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-green-600 flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4" /> Advantages
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Captures INSERT, UPDATE, DELETE</li>
                        <li>• Oracle-native feature</li>
                        <li>• Works through firewalls</li>
                        <li>• Reliable change tracking</li>
                        <li>• Supports Arabic/Unicode</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-yellow-600 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" /> Requirements
                      </h5>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• DBA must create MView logs</li>
                        <li>• Primary key required on tables</li>
                        <li>• Storage overhead for logs</li>
                        <li>• Log maintenance required</li>
                        <li>• Per-table configuration</li>
                      </ul>
                    </div>
                  </div>

                  <CodeBlock 
                    title="DBA: Create Materialized View Log"
                    code={`-- Create MView log on source table (run by DBA)
CREATE MATERIALIZED VIEW LOG ON APPS.AR_INVOICES
WITH ROWID, PRIMARY KEY, SEQUENCE
INCLUDING NEW VALUES;

-- For tables with specific columns to track:
CREATE MATERIALIZED VIEW LOG ON APPS.AR_PAYMENTS
WITH ROWID, PRIMARY KEY (PAYMENT_ID), SEQUENCE
(PAYMENT_AMOUNT, PAYMENT_DATE, STATUS, CUSTOMER_ID)
INCLUDING NEW VALUES;

-- Grant access to CDC user
GRANT SELECT ON APPS.MLOG$_AR_INVOICES TO dataflow_user;`}
                  />

                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h5 className="font-semibold text-orange-700 mb-2">When to Use</h5>
                    <p className="text-sm text-orange-600">
                      Use Materialized View Logs when you need to capture DELETEs but cannot use Google Datastream,
                      and your DBA can create and maintain the MView logs on source tables.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Comparison Table */}
              <SectionCard icon={Table} title="CDC Methods Comparison">
                <ComparisonTable
                  headers={["Feature", "Google Datastream", "Timestamp Polling", "MView Logs"]}
                  rows={[
                    ["Latency", "5-15 seconds", "1-60 minutes", "1-5 minutes"],
                    ["Captures INSERTs", "✅ Yes", "✅ Yes", "✅ Yes"],
                    ["Captures UPDATEs", "✅ Yes", "✅ Yes", "✅ Yes"],
                    ["Captures DELETEs", "✅ Yes", "❌ No", "✅ Yes"],
                    ["Source DB Impact", "Minimal", "Moderate", "Low"],
                    ["Setup Complexity", "Medium", "Low", "Medium"],
                    ["Network Required", "Google Cloud access", "Any connectivity", "Any connectivity"],
                    ["DBA Involvement", "One-time setup", "None", "Per-table setup"],
                    ["Unicode/Arabic", "✅ Full support", "✅ Full support", "✅ Full support"],
                    ["Best For", "Real-time analytics", "Simple sync", "Full CDC w/o cloud access"],
                  ]}
                />
              </SectionCard>
            </div>
          )}

          {/* Transformations Section */}
          {activeSection === "transformations" && (
            <div className="space-y-6">
              <SectionCard icon={Layers} title="Understanding Transformations" variant="highlight">
                <p className="text-muted-foreground mb-4">
                  Transformations convert raw CDC data into cleaned, business-ready datasets. 
                  Create transformations once and run them for any tenant.
                </p>
                
                <FlowDiagram steps={[
                  { label: "Bronze (CDC)", color: "bg-amber-100 text-amber-700" },
                  { label: "Silver Transform", color: "bg-gray-100 text-gray-700" },
                  { label: "Gold Transform", color: "bg-yellow-200 text-yellow-800" },
                  { label: "BI/Analytics", color: "bg-green-100 text-green-700" },
                ]} />
              </SectionCard>

              {/* Silver Transformations */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Layers className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <CardTitle>Silver Transformations</CardTitle>
                      <CardDescription>Clean, Cast, and Standardize</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Silver transformations clean and standardize raw Bronze data. This includes data type casting,
                    null handling, filtering deleted records, and renaming columns.
                  </p>

                  <h5 className="font-semibold">What Silver Transformations Do:</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Data Type Casting</p>
                      <p className="text-xs text-muted-foreground">Convert VARCHAR dates to DATE, strings to numbers</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Column Renaming</p>
                      <p className="text-xs text-muted-foreground">Standardize column names across sources</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Filter Deleted Records</p>
                      <p className="text-xs text-muted-foreground">Automatically excludes _metadata_deleted = TRUE</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Add Tenant ID</p>
                      <p className="text-xs text-muted-foreground">Preserves tenant_id for multi-tenant isolation</p>
                    </div>
                  </div>

                  <CodeBlock 
                    title="Generated Silver Query Example"
                    code={`SELECT
    tenant_id,
    SAFE_CAST(INVOICE_ID AS INT64) AS invoice_id,
    SAFE_CAST(INVOICE_DATE AS DATE) AS invoice_date,
    SAFE_CAST(AMOUNT AS FLOAT64) AS amount,
    CUSTOMER_NAME AS customer_name,
    STATUS AS status,
    _metadata_timestamp AS cdc_timestamp,
    _metadata_deleted AS cdc_deleted
FROM \`project.cdc_bronze.AR_INVOICES\`
WHERE IFNULL(_metadata_deleted, FALSE) = FALSE`}
                  />
                </CardContent>
              </Card>

              {/* Gold Transformations */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Layers className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle>Gold Transformations</CardTitle>
                      <CardDescription>Aggregate, Join, and Calculate KPIs</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Gold transformations create business-ready datasets by joining multiple tables,
                    calculating aggregations, and building KPIs. These tables are typically protected by RLS.
                  </p>

                  <h5 className="font-semibold">What Gold Transformations Do:</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Join Multiple Tables</p>
                      <p className="text-xs text-muted-foreground">Combine invoices with customers, payments, etc.</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Aggregations</p>
                      <p className="text-xs text-muted-foreground">SUM, COUNT, AVG, MIN, MAX by dimensions</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Calculated Fields</p>
                      <p className="text-xs text-muted-foreground">Profit margins, conversion rates, etc.</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="font-medium text-sm">Auto-Apply RLS</p>
                      <p className="text-xs text-muted-foreground">Optionally create RLS policy on completion</p>
                    </div>
                  </div>

                  <CodeBlock 
                    title="Generated Gold Query Example"
                    code={`SELECT
    t0.tenant_id,
    t0.customer_id,
    t1.customer_name,
    t1.region,
    COUNT(t0.invoice_id) AS total_invoices,
    SUM(t0.amount) AS total_revenue,
    AVG(t0.amount) AS avg_invoice_amount
FROM \`project.cdc_silver.clean_invoices\` AS t0
LEFT JOIN \`project.cdc_silver.clean_customers\` AS t1
    ON t0.customer_id = t1.customer_id
GROUP BY t0.tenant_id, t0.customer_id, t1.customer_name, t1.region`}
                  />
                </CardContent>
              </Card>

              {/* Merge Strategies */}
              <SectionCard icon={RefreshCw} title="Merge Strategies">
                <p className="text-muted-foreground mb-4">
                  When running transformations for multiple tenants, the merge strategy determines how 
                  data is written to shared Silver/Gold tables.
                </p>

                <div className="space-y-4">
                  <div className="p-4 border rounded-lg border-green-200 bg-green-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-100 text-green-700">Recommended</Badge>
                      <h5 className="font-semibold">Merge by Tenant</h5>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Deletes existing data for the specific tenant, then inserts new data.
                      Other tenants&apos; data remains untouched.
                    </p>
                    <CodeBlock 
                      code={`-- Step 1: Delete existing tenant data
DELETE FROM gold_table WHERE tenant_id = 'TENANT_A';

-- Step 2: Insert new tenant data
INSERT INTO gold_table SELECT * FROM transform_query;`}
                    />
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h5 className="font-semibold mb-2">Replace All</h5>
                    <p className="text-sm text-muted-foreground mb-2">
                      Truncates the entire table and replaces with new data.
                      Use only when running for all tenants simultaneously.
                    </p>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Warning: This will delete ALL tenants&apos; data before inserting.
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Multi-Tenant Section */}
          {activeSection === "multi-tenant" && (
            <div className="space-y-6">
              <SectionCard icon={Building2} title="Multi-Tenant Architecture" variant="highlight">
                <p className="text-muted-foreground mb-4">
                  DataFlow uses a dynamic multi-tenant model where pipelines and transformations are 
                  defined once and executed for any tenant. The tenant context is injected at runtime.
                </p>

                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <h5 className="font-semibold mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" /> Key Concept
                  </h5>
                  <p className="text-sm">
                    <strong>Tenants</strong> represent your customers/clients (identified by your CRM&apos;s tenant_id).
                    Each tenant&apos;s data is isolated in the Bronze layer and tagged with tenant_id in Silver/Gold layers.
                  </p>
                </div>
              </SectionCard>

              {/* How it Works */}
              <Card>
                <CardHeader>
                  <CardTitle>How Multi-Tenancy Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <StepCard number="1" title="Register Tenants">
                      Go to <strong>Tenants</strong> page and add your tenants with their CRM tenant IDs.
                      Each tenant can have multiple accounting units (optional).
                    </StepCard>

                    <StepCard number="2" title="Create Pipeline/Transformation Once">
                      Build your Bronze → Silver → Gold pipeline or CDC transformation.
                      Do NOT hardcode any tenant information - the pipeline is generic.
                    </StepCard>

                    <StepCard number="3" title="Run for Specific Tenant">
                      Click <strong>&quot;Run for Tenant&quot;</strong> and select the tenant.
                      The system automatically:
                      <ul className="mt-2 space-y-1">
                        <li>• Routes Bronze data to tenant-specific dataset</li>
                        <li>• Tags Silver/Gold data with tenant_id</li>
                        <li>• Uses MERGE to preserve other tenants&apos; data</li>
                      </ul>
                    </StepCard>

                    <StepCard number="4" title="Apply RLS (Optional)">
                      Create Row-Level Security policies to restrict data access.
                      Each tenant&apos;s users can only see their own data.
                    </StepCard>
                  </div>
                </CardContent>
              </Card>

              {/* Data Isolation Strategy */}
              <Card>
                <CardHeader>
                  <CardTitle>Data Isolation Strategy</CardTitle>
                </CardHeader>
                <CardContent>
                  <ComparisonTable
                    headers={["Layer", "Isolation Method", "Example"]}
                    rows={[
                      [
                        <Badge key="bronze" className="bg-amber-100 text-amber-700">Bronze</Badge>,
                        "Separate datasets per tenant",
                        <code key="b-ex" className="text-xs bg-muted px-2 py-1 rounded">bronze_tenant_a, bronze_tenant_b</code>
                      ],
                      [
                        <Badge key="silver" className="bg-gray-100 text-gray-700">Silver</Badge>,
                        "Shared tables with tenant_id column",
                        <code key="s-ex" className="text-xs bg-muted px-2 py-1 rounded">datalake_silver.clean_invoices (tenant_id = &apos;TENANT_A&apos;)</code>
                      ],
                      [
                        <Badge key="gold" className="bg-yellow-200 text-yellow-800">Gold</Badge>,
                        "Shared tables + RLS policies",
                        <code key="g-ex" className="text-xs bg-muted px-2 py-1 rounded">datalake_gold.revenue_summary + RLS filter</code>
                      ],
                    ]}
                  />

                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <h5 className="font-semibold mb-3">Visual Representation</h5>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="w-24">Tenant A</Badge>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-amber-100 px-2 py-1 rounded">bronze_tenant_a.*</code>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">silver.* WHERE tenant_id=&apos;A&apos;</code>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-yellow-100 px-2 py-1 rounded">gold.* + RLS(A)</code>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="w-24">Tenant B</Badge>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-amber-100 px-2 py-1 rounded">bronze_tenant_b.*</code>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">silver.* WHERE tenant_id=&apos;B&apos;</code>
                        <ArrowRight className="w-4 h-4" />
                        <code className="text-xs bg-yellow-100 px-2 py-1 rounded">gold.* + RLS(B)</code>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Best Practices */}
              <SectionCard icon={CheckCircle2} title="Multi-Tenant Best Practices">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Use &quot;Merge by Tenant&quot; strategy</p>
                      <p className="text-sm text-muted-foreground">Prevents accidental deletion of other tenants&apos; data</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Enable &quot;Auto-Apply RLS&quot; for Gold tables</p>
                      <p className="text-sm text-muted-foreground">Automatically creates security policies per tenant</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Use external CRM tenant IDs</p>
                      <p className="text-sm text-muted-foreground">Keep tenant IDs consistent with your CRM system</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Never hardcode tenant IDs in pipelines</p>
                      <p className="text-sm text-muted-foreground">Pipelines should be tenant-agnostic; select tenant at runtime</p>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>
          )}

          {/* RLS Section */}
          {activeSection === "rls" && (
            <div className="space-y-6">
              <SectionCard icon={Shield} title="Row-Level Security (RLS)" variant="highlight">
                <p className="text-muted-foreground mb-4">
                  Row-Level Security in BigQuery restricts which rows users can access based on 
                  filter conditions. DataFlow creates RLS policies that filter by tenant_id.
                </p>
              </SectionCard>

              <Card>
                <CardHeader>
                  <CardTitle>How RLS Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 border rounded-lg">
                      <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                      <p className="font-medium">User Queries Table</p>
                      <p className="text-xs text-muted-foreground">SELECT * FROM gold.revenue</p>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <Shield className="w-8 h-8 mx-auto mb-2 text-green-500" />
                      <p className="font-medium">RLS Filter Applied</p>
                      <p className="text-xs text-muted-foreground">WHERE tenant_id = &apos;USER_TENANT&apos;</p>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <Table className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                      <p className="font-medium">Only Tenant Data</p>
                      <p className="text-xs text-muted-foreground">User sees only their tenant&apos;s rows</p>
                    </div>
                  </div>

                  <CodeBlock 
                    title="RLS Policy Created by DataFlow"
                    code={`-- BigQuery Row Access Policy
CREATE ROW ACCESS POLICY rls_tenant_a_revenue
ON \`project.datalake_gold.revenue_summary\`
GRANT TO ("allAuthenticatedUsers")
FILTER USING (tenant_id = 'TENANT_A');

-- When user runs: SELECT * FROM revenue_summary
-- BigQuery automatically adds: WHERE tenant_id = 'TENANT_A'`}
                  />

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="font-semibold text-blue-700 mb-2">Creating RLS Policies</h5>
                    <ol className="text-sm text-blue-600 space-y-1">
                      <li>1. Go to the <strong>RLS</strong> page</li>
                      <li>2. Select the Gold table to protect</li>
                      <li>3. Choose the tenant for this policy</li>
                      <li>4. Click <strong>Create Policy</strong></li>
                      <li>5. Click <strong>Apply to BigQuery</strong></li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Examples Section */}
          {activeSection === "examples" && (
            <div className="space-y-6">
              <SectionCard icon={FileText} title="Complete Example: Multi-Tenant Invoice Processing" variant="highlight">
                <p className="text-muted-foreground">
                  This example walks through setting up a complete multi-tenant data pipeline for 
                  processing invoices from Oracle ERP to BigQuery.
                </p>
              </SectionCard>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="step1" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                      <span className="font-semibold">Setup Tenants</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        First, register your tenants in the system. Each tenant represents a customer in your CRM.
                      </p>
                      <CodeBlock 
                        title="Example Tenants"
                        code={`Tenant 1:
  Name: Al Rajhi Group
  Tenant ID: AL_RAJHI_GRP  (from your CRM)
  Units: Industrial_DB, Retail_DB

Tenant 2:
  Name: Saudi Telecom
  Tenant ID: STC_CORP
  Units: Consumer_BU, Enterprise_BU`}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step2" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                      <span className="font-semibold">Create CDC Stream</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Set up a CDC stream to replicate the AR_INVOICES table from Oracle.
                      </p>
                      <div className="p-4 bg-muted rounded-lg space-y-2">
                        <p><strong>Stream Name:</strong> Invoice CDC Stream</p>
                        <p><strong>CDC Method:</strong> Google Datastream (recommended)</p>
                        <p><strong>Source:</strong> APPS.AR_INVOICES</p>
                        <p><strong>Destination Dataset:</strong> cdc_bronze</p>
                        <p><strong>Multi-Tenant Options:</strong></p>
                        <ul className="ml-4 text-sm">
                          <li>• Tenant Column Name: tenant_id</li>
                          <li>• Route by Tenant: ✓ Enabled</li>
                          <li>• Auto-Apply RLS on Gold: ✓ Enabled</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step3" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</div>
                      <span className="font-semibold">Create Silver Transformation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Create a Silver transformation to clean and standardize invoice data.
                      </p>
                      <div className="p-4 bg-muted rounded-lg space-y-2">
                        <p><strong>Name:</strong> Clean Invoices</p>
                        <p><strong>Type:</strong> Silver</p>
                        <p><strong>Source Table:</strong> AR_INVOICES (from CDC)</p>
                        <p><strong>Target:</strong> cdc_silver.clean_invoices</p>
                        <p><strong>Field Mappings:</strong></p>
                        <ul className="ml-4 text-sm font-mono">
                          <li>• INVOICE_ID → invoice_id (INT64)</li>
                          <li>• INVOICE_DATE → invoice_date (DATE)</li>
                          <li>• TOTAL_AMOUNT → amount (FLOAT64)</li>
                          <li>• CUST_NAME → customer_name (STRING)</li>
                        </ul>
                        <p className="mt-2"><strong>Tenant Options:</strong></p>
                        <ul className="ml-4 text-sm">
                          <li>• Tenant-Aware: ✓ Yes</li>
                          <li>• Merge Strategy: Merge by Tenant</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step4" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">4</div>
                      <span className="font-semibold">Create Gold Transformation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Create a Gold transformation to build revenue analytics.
                      </p>
                      <div className="p-4 bg-muted rounded-lg space-y-2">
                        <p><strong>Name:</strong> Revenue Summary</p>
                        <p><strong>Type:</strong> Gold</p>
                        <p><strong>Source Table:</strong> clean_invoices (Silver)</p>
                        <p><strong>Target:</strong> cdc_gold.revenue_summary</p>
                        <p><strong>Aggregations:</strong></p>
                        <ul className="ml-4 text-sm font-mono">
                          <li>• COUNT(invoice_id) → total_invoices</li>
                          <li>• SUM(amount) → total_revenue</li>
                          <li>• AVG(amount) → avg_invoice</li>
                        </ul>
                        <p className="mt-2"><strong>Tenant Options:</strong></p>
                        <ul className="ml-4 text-sm">
                          <li>• Tenant-Aware: ✓ Yes</li>
                          <li>• Merge Strategy: Merge by Tenant</li>
                          <li>• Auto-Apply RLS: ✓ Yes</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step5" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">5</div>
                      <span className="font-semibold">Run for Each Tenant</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Execute transformations for each tenant. The same transformation runs for all tenants!
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border rounded-lg">
                          <h5 className="font-semibold mb-2 flex items-center gap-2">
                            <Play className="w-4 h-4" /> Run for Tenant A
                          </h5>
                          <p className="text-sm text-muted-foreground mb-2">
                            Click &quot;Run for Tenant&quot; → Select &quot;Al Rajhi Group&quot;
                          </p>
                          <p className="text-xs font-mono bg-muted p-2 rounded">
                            Result: tenant_id = &apos;AL_RAJHI_GRP&apos; data processed
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <h5 className="font-semibold mb-2 flex items-center gap-2">
                            <Play className="w-4 h-4" /> Run for Tenant B
                          </h5>
                          <p className="text-sm text-muted-foreground mb-2">
                            Click &quot;Run for Tenant&quot; → Select &quot;Saudi Telecom&quot;
                          </p>
                          <p className="text-xs font-mono bg-muted p-2 rounded">
                            Result: tenant_id = &apos;STC_CORP&apos; data processed
                          </p>
                        </div>
                      </div>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700">
                          <CheckCircle2 className="w-4 h-4 inline mr-1" />
                          Both tenants&apos; data now exists in the same Gold table, isolated by tenant_id and protected by RLS!
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step6" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">6</div>
                      <span className="font-semibold">Verify RLS Protection</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Check that RLS policies are active and protecting tenant data.
                      </p>
                      <CodeBlock 
                        title="Test RLS in BigQuery"
                        code={`-- As Al Rajhi user:
SELECT * FROM datalake_gold.revenue_summary;
-- Returns ONLY Al Rajhi data (RLS filter applied automatically)

-- As STC user:
SELECT * FROM datalake_gold.revenue_summary;
-- Returns ONLY STC data (different RLS filter applied)

-- As Admin (no RLS):
SELECT tenant_id, COUNT(*) as rows
FROM datalake_gold.revenue_summary
GROUP BY tenant_id;
-- Returns:
-- AL_RAJHI_GRP: 1,250 rows
-- STC_CORP: 3,420 rows`}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
